// Format validation and formatting functions
const formatValidators = {
  aadhar_card: {
    pattern: /^\d{12}$/,
    format: (value) => value.replace(/\D/g, '').slice(0, 12),
    formatHint: '12 digits only (e.g., 123456789012)',
    validate: (value) => /^\d{12}$/.test(value)
  },
  pan_card: {
    pattern: /^[A-Z]{5}\d{4}[A-Z]{1}$/,
    format: (value) => value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10),
    formatHint: 'Format: ABCDE1234F (5 letters, 4 digits, 1 letter - all uppercase)',
    validate: (value) => /^[A-Z]{5}\d{4}[A-Z]{1}$/.test(value)
  },
  voter_id: {
    pattern: /^[A-Z]{3}\d{7}$/,
    format: (value) => value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10),
    formatHint: 'Format: ABC1234567 (3 letters, 7 digits - all uppercase)',
    validate: (value) => /^[A-Z]{3}\d{7}$/.test(value)
  },
  ssn: {
    pattern: /^\d{3}-\d{2}-\d{4}$/,
    format: (value) => {
      const cleaned = value.replace(/\D/g, '').slice(0, 9);
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
    },
    formatHint: 'Format: XXX-XX-XXXX (e.g., 123-45-6789)',
    validate: (value) => /^\d{3}-\d{2}-\d{4}$/.test(value)
  },
  drivers_license: {
    pattern: /^.{6,20}$/,
    format: (value) => value.toUpperCase().slice(0, 20),
    formatHint: '6-20 characters (uppercase)',
    validate: (value) => /^.{6,20}$/.test(value)
  },
  nin: {
    pattern: /^\d{11}$/,
    format: (value) => value.replace(/\D/g, '').slice(0, 11),
    formatHint: '11 digits only (e.g., 12345678901)',
    validate: (value) => /^\d{11}$/.test(value)
  },
  national_insurance: {
    pattern: /^[A-Z]{2}\d{6}[A-Z]{1}$/,
    format: (value) => value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 9),
    formatHint: 'Format: AB123456C (2 letters, 6 digits, 1 letter - all uppercase)',
    validate: (value) => /^[A-Z]{2}\d{6}[A-Z]{1}$/.test(value)
  },
  id_card: {
    pattern: /^.{8,18}$/,
    format: (value) => value.toUpperCase().slice(0, 18),
    formatHint: '8-18 characters',
    validate: (value) => /^.{8,18}$/.test(value)
  },
  my_number: {
    pattern: /^\d{12}$/,
    format: (value) => value.replace(/\D/g, '').slice(0, 12),
    formatHint: '12 digits only',
    validate: (value) => /^\d{12}$/.test(value)
  },
  id_number: {
    pattern: /^\d{13}$/,
    format: (value) => value.replace(/\D/g, '').slice(0, 13),
    formatHint: '13 digits only',
    validate: (value) => /^\d{13}$/.test(value)
  }
};

// Country-specific identification fields mapping
export const countryIdentificationFields = {
  '+91': { // India
    country: 'India',
    fields: [
      { 
        name: 'aadhar_card', 
        label: 'Aadhar Card Number', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter 12-digit Aadhar number',
        validator: formatValidators.aadhar_card
      },
      { 
        name: 'pan_card', 
        label: 'PAN Card Number', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter PAN number (e.g., ABCDE1234F)',
        validator: formatValidators.pan_card
      },
      { 
        name: 'voter_id', 
        label: 'Voter ID Number', 
        type: 'text', 
        required: false, 
        placeholder: 'Enter Voter ID number',
        validator: formatValidators.voter_id
      }
    ]
  },
  '+1': { // US/Canada
    country: 'United States/Canada',
    fields: [
      { 
        name: 'ssn', 
        label: 'Social Security Number (SSN)', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter SSN (XXX-XX-XXXX)', 
        validator: formatValidators.ssn
      },
      { 
        name: 'drivers_license', 
        label: "Driver's License Number", 
        type: 'text', 
        required: true, 
        placeholder: "Enter driver's license number",
        validator: formatValidators.drivers_license
      },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+234': { // Nigeria
    country: 'Nigeria',
    fields: [
      { name: 'national_id', label: 'National ID Number', type: 'text', required: true, placeholder: 'Enter National ID number' },
      { 
        name: 'nin', 
        label: 'NIN (National Identification Number)', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter 11-digit NIN',
        validator: formatValidators.nin
      },
      { name: 'voters_card', label: "Voter's Card Number", type: 'text', required: false, placeholder: "Enter voter's card number" }
    ]
  },
  '+44': { // UK
    country: 'United Kingdom',
    fields: [
      { 
        name: 'national_insurance', 
        label: 'National Insurance Number', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter NI number (e.g., AB123456C)',
        validator: formatValidators.national_insurance
      },
      { name: 'passport', label: 'Passport Number', type: 'text', required: true, placeholder: 'Enter passport number' },
      { name: 'driving_license', label: "Driving License Number", type: 'text', required: false, placeholder: "Enter driving license number" }
    ]
  },
  '+33': { // France
    country: 'France',
    fields: [
      { name: 'cni', label: 'CNI (Carte Nationale d\'Identité)', type: 'text', required: true, placeholder: 'Enter CNI number' },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+49': { // Germany
    country: 'Germany',
    fields: [
      { name: 'id_card', label: 'ID Card Number', type: 'text', required: true, placeholder: 'Enter ID card number' },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+86': { // China
    country: 'China',
    fields: [
      { 
        name: 'id_card', 
        label: 'ID Card Number (身份证)', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter 18-digit ID card number',
        validator: formatValidators.id_card
      },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+81': { // Japan
    country: 'Japan',
    fields: [
      { 
        name: 'my_number', 
        label: 'My Number (個人番号)', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter 12-digit My Number',
        validator: formatValidators.my_number
      },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+61': { // Australia
    country: 'Australia',
    fields: [
      { name: 'medicare', label: 'Medicare Number', type: 'text', required: true, placeholder: 'Enter Medicare number' },
      { name: 'drivers_license', label: "Driver's License Number", type: 'text', required: true, placeholder: "Enter driver's license number" },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  },
  '+27': { // South Africa
    country: 'South Africa',
    fields: [
      { 
        name: 'id_number', 
        label: 'ID Number', 
        type: 'text', 
        required: true, 
        placeholder: 'Enter 13-digit ID number',
        validator: formatValidators.id_number
      },
      { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
    ]
  }
};

/**
 * Format identification field value based on field type
 * @param {string} fieldName - Field name
 * @param {string} value - Current value
 * @returns {string} - Formatted value
 */
export const formatIdentificationValue = (fieldName, value) => {
  const validator = formatValidators[fieldName];
  if (validator && validator.format) {
    return validator.format(value);
  }
  return value;
};

/**
 * Validate identification field value
 * @param {string} fieldName - Field name
 * @param {string} value - Value to validate
 * @returns {boolean} - True if valid
 */
export const validateIdentificationValue = (fieldName, value) => {
  if (!value) return false;
  const validator = formatValidators[fieldName];
  if (validator && validator.validate) {
    return validator.validate(value);
  }
  return true; // No validator means any value is acceptable
};

// Default fields if country not found
export const defaultIdentificationFields = {
  country: 'Other',
  fields: [
    { name: 'national_id', label: 'National ID Number', type: 'text', required: true, placeholder: 'Enter national ID number' },
    { name: 'passport', label: 'Passport Number', type: 'text', required: false, placeholder: 'Enter passport number' }
  ]
};

/**
 * Extract country code from phone number
 * @param {string} phoneNumber - Phone number with country code (e.g., "+1234567890")
 * @returns {string} - Country code (e.g., "+1")
 */
export const getCountryCodeFromPhone = (phoneNumber) => {
  if (!phoneNumber) return null;
  
  // Remove spaces, hyphens, parentheses
  const cleaned = phoneNumber.replace(/[\s\-()]/g, '');
  
  // Check for country codes (longest first to match +234 before +2)
  const codes = Object.keys(countryIdentificationFields).sort((a, b) => b.length - a.length);
  
  for (const code of codes) {
    if (cleaned.startsWith(code)) {
      return code;
    }
  }
  
  // Default to +1 if starts with + but no match
  if (cleaned.startsWith('+')) {
    return '+1';
  }
  
  return null;
};

/**
 * Get identification fields for a country code
 * @param {string} countryCode - Country code (e.g., "+91")
 * @returns {object} - Country identification fields configuration
 */
export const getIdentificationFieldsForCountry = (countryCode) => {
  if (!countryCode) {
    return defaultIdentificationFields;
  }
  
  return countryIdentificationFields[countryCode] || defaultIdentificationFields;
};

